import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// File size formatting
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Date formatting
export function formatDate(date: Date | string): string {
  const d = new Date(date)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Generate SHA-256 hash
export async function generateSHA256Hash(data: string | ArrayBuffer): Promise<string> {
  const buffer = typeof data === 'string' ? new TextEncoder().encode(data) : data
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

// Validate file type
export function validateFileType(file: File, allowedTypes: string[]): boolean {
  return allowedTypes.includes(file.type)
}

// Validate file size
export function validateFileSize(file: File, maxSize: number): boolean {
  return file.size <= maxSize
}

// Generate unique ID
export function generateId(): string {
  return Math.random().toString(36).substr(2, 9)
}

// Debounce function
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// Throttle function
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

// Deep clone object
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Date) return new Date(obj.getTime()) as T
  if (obj instanceof Array) return obj.map(item => deepClone(item)) as T
  if (typeof obj === 'object') {
    const clonedObj = {} as T
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key])
      }
    }
    return clonedObj
  }
  return obj
} 

/**
 * Calculate SHA-256 hash for DRM protection
 */
export const calculateFileHash = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

/**
 * Validate media file for DRM compatibility
 */
export const validateMediaFile = (file: File): { isValid: boolean; error?: string } => {
  const validVideoTypes = [
    'video/mp4', 
    'video/x-matroska', 
    'video/avi',
    'video/mov',
    'video/wmv'
  ]
  
  const validAudioTypes = [
    'audio/mp3', 
    'audio/wav', 
    'audio/aac', 
    'audio/mpeg',
    'audio/x-m4a',
    'audio/m4a',
    'audio/flac'
  ]

  if (file.type.startsWith('video/') && !validVideoTypes.includes(file.type)) {
    return { isValid: false, error: 'Please select a valid video file (MP4, MKV, AVI, MOV, or WMV)' }
  }

  if (file.type.startsWith('audio/') && !validAudioTypes.includes(file.type)) {
    return { isValid: false, error: 'Please select a valid audio file (MP3, WAV, AAC, M4A, or FLAC)' }
  }

  return { isValid: true }
};

/**
 * Extract media duration for DRM metadata
 */
export const extractMediaDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    if (file.type.startsWith('image/')) {
      resolve(0) // Images don't have duration
    } else if (file.type.startsWith('video/')) {
      const video = document.createElement('video')
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src)
        resolve(video.duration)
      }
      video.onerror = () => {
        URL.revokeObjectURL(video.src)
        reject(new Error('Failed to extract video duration'))
      }
      video.src = URL.createObjectURL(file)
    } else if (file.type.startsWith('audio/')) {
      const audio = new Audio()
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(audio.src)
        resolve(audio.duration)
      }
      audio.onerror = () => {
        URL.revokeObjectURL(audio.src)
        reject(new Error('Failed to extract audio duration'))
      }
      audio.src = URL.createObjectURL(file)
    } else {
      resolve(0)
    }
  })
};

/**
 * Extract media dimensions for DRM metadata
 */
export const extractMediaDimensions = (file: File): Promise<{ width: number; height: number } | null> => {
  return new Promise((resolve, reject) => {
    if (file.type.startsWith('image/')) {
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)
        resolve({ width: img.width, height: img.height })
      }
      img.onerror = () => {
        URL.revokeObjectURL(img.src)
        reject(new Error('Failed to extract image dimensions'))
      }
      img.src = URL.createObjectURL(file)
    } else if (file.type.startsWith('video/')) {
      const video = document.createElement('video')
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src)
        resolve({ width: video.videoWidth, height: video.videoHeight })
      }
      video.onerror = () => {
        URL.revokeObjectURL(video.src)
        reject(new Error('Failed to extract video dimensions'))
      }
      video.src = URL.createObjectURL(file)
    } else {
      resolve(null) // Audio files don't have dimensions
    }
  })
}; 

/**
 * Extract basic audio metadata without external dependencies
 */
export const extractAudioMetadata = (file: File): Promise<{
  title?: string
  artist?: string
  album?: string
  duration?: number
}> => {
  return new Promise(async (resolve) => {
    const metadata: {
      title?: string
      artist?: string
      album?: string
      duration?: number
    } = {}

    // Extract duration
    try {
      metadata.duration = await extractMediaDuration(file)
    } catch (error) {
      console.warn('Failed to extract audio duration:', error)
    }

    // For now, use filename as title if no ID3 tags are available
    metadata.title = file.name.replace(/\.[^/.]+$/, '') // Remove file extension

    resolve(metadata)
  })
} 